import { spanAria, spanOn, spanTime } from "../lib/span.js";
import type { Occurrence } from "../lib/types.js";
import { PersonDiscs } from "./PersonDiscs.js";

/**
 * One event in the week list.
 *
 * A background commitment — school — renders in soft ink rather than full
 * ink. It is context, not something anyone has to do, and at full weight it
 * dominates every day it appears on.
 *
 * A row also has to say *which day of the event* it is. The same trip shows up
 * on every day it covers, and five rows reading "Svetlana in Denver · all day"
 * tell you she is away without ever telling you when she is back. The rail
 * down the left edge carries between days, the time column says how far in
 * this day is, and together they read as one thing rather than five.
 */
export function EventRow({
  occurrence,
  date,
  zone,
  onOpen,
}: {
  occurrence: Occurrence;
  /** The day this row is standing in for. */
  date: string;
  zone: string;
  onOpen: () => void;
}): React.JSX.Element {
  const background = occurrence.is_background;
  const span = spanOn(occurrence, date, zone);
  const lead = occurrence.people[0];

  const classes = [
    "event-row",
    background ? "is-background" : "",
    span.spans ? "is-spanning" : "",
    span.fromEarlier ? "is-from-earlier" : "",
    span.intoLater ? "is-into-later" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type="button" className={classes} onClick={onOpen}>
      {/* The rail is the continuity. It runs to the row's edges on any side
          the event carries on past, so consecutive days join up the list. */}
      {span.spans && (
        <span
          className="event-row__rail"
          style={{ background: lead?.color ?? "var(--mid)", opacity: background ? 0.45 : 1 }}
        />
      )}

      <PersonDiscs people={occurrence.people} size={17} muted={background} />

      <span className="event-row__body">
        <span className="event-row__title">{occurrence.title}</span>
        {occurrence.location !== null && (
          <span className="event-row__where">{occurrence.location}</span>
        )}
      </span>

      <span
        className={`event-row__time${occurrence.all_day ? " is-allday" : ""}${
          span.spans ? " is-spanning" : ""
        }`}
      >
        {spanTime(occurrence, date, zone)}
      </span>

      <span className="sr-only">{spanAria(occurrence, date, zone)}</span>
    </button>
  );
}
